<script lang="ts" setup>
import { IYearMonth } from '@/coreDomainsModule/date';
import { ForecastForReceiptHeaderKey, TableContentProperty } from '../../interface/tableContentHeaders';
import { TableContentItem } from '../../interface/tableContentItems';
import TableCell from '../TableCell.vue';
import SupplierForecastCreationStatusIcon from './SupplierForecastCreationStatusIcon.vue';
import ForecastRecoveryStatusIcon from './ForecastRecoveryStatusIcon.vue';
import { ForecastForReceiptTableContentViewModel } from './forecastForReceiptTableContentViewModel';
import { computed } from 'vue';
import TextButton from '@/components/input/Btn/TextButton.vue';
import ForecastForReceiptRowMenu from './ForecastForReceiptRowMenu.vue';
import StaffFilterRowMenu from './StaffFilterRowMenu.vue';
import { i18n } from '@/i18n';

const t = i18n.t;

// props
const props = withDefaults(
  defineProps<{
    header: TableContentProperty;
    headerKey: ForecastForReceiptHeaderKey;
    item: TableContentItem;
    headerIndex: number;
    accumulationTableContentWidth: number;
    isVariableWidth: boolean;
    isAllSelected: boolean;
    stableColumnCount: number;
    isBold: boolean;
    forecastStartYearMonth?: IYearMonth;
    isChecked: boolean;
    isCheckedBusinessId: boolean;
  }>(),
  {
    forecastStartYearMonth: undefined,
  }
);

// emits
const emits = defineEmits<{
  (e: 'onClickNarrow'): void;
  (e: 'onClickTransitionTimeline'): void;
  (e: 'onClickTransactionList'): void;
  (e: 'onClickDownload'): void;
  (e: 'onClickForecastRequestMail'): void;
  (e: 'onClickEdit'): void;
  (e: 'onClickRemoveFromForecast'): void;
}>();

const forecastForReceiptTableContentVM = new ForecastForReceiptTableContentViewModel();

const textAlign = computed(() => {
  if (props.header.key === 'SupplierForecastCreateStatus') {
    return 'center';
  }
  if (props.header.key === 'ForecastForReceiptRecoverStatus' && props.item.forecastForReceiptId) {
    return 'center';
  }
  return props.header.propertyPath?.propertyType === 'number' ? 'right' : 'left';
});
</script>

<template>
  <TableCell
    :class="[forecastForReceiptTableContentVM.isSticky(stableColumnCount, headerIndex) ? 'sticky-content' : '']"
    :style="{
      left: forecastForReceiptTableContentVM.calcStickyDiff(stableColumnCount, headerIndex, accumulationTableContentWidth),
    }"
    class="table-cell base-table__body--fixed"
    :text-align="textAlign"
    :table-content-property="header"
    :tool-tip-value="forecastForReceiptTableContentVM.displayForecastForReceiptItem(headerKey, item, forecastStartYearMonth)"
    :is-highlighted="props.isChecked || props.isCheckedBusinessId"
  >
    <SupplierForecastCreationStatusIcon v-if="headerKey === 'SupplierForecastCreateStatus'" :is-created="item.createdSupplierForecast" />
    <ForecastRecoveryStatusIcon
      v-else-if="headerKey === 'ForecastForReceiptRecoverStatus' && item.forecastForReceiptId"
      :is-recovered="(item.recoveredForecastCount ?? 0) >= (item.totalForecastCount ?? 0) && (item.totalForecastCount ?? 0) > 0"
    />
    <TextButton v-else-if="headerKey === 'timelineButton'" class="transition-timeline-button" @click="emits('onClickTransitionTimeline')">
      <p class="transition-timeline-button__content-text">{{ t('内示遍歴') }}</p>
    </TextButton>
    <ForecastForReceiptRowMenu
      v-else-if="headerKey === 'ForecastForReceiptRowMenu'"
      @on-click-transaction-list="emits('onClickTransactionList')"
      @on-select-menu="
        (option) => {
          if (option === 'download') emits('onClickDownload');
          if (option === 'forecastRequestMail') emits('onClickForecastRequestMail');
          if (option === 'edit') emits('onClickEdit');
        }
      "
    />
    <StaffFilterRowMenu
      v-else-if="headerKey === 'ForecastForReceiptEditOption'"
      @on-select-menu="
        (option) => {
          if (option === 'download') emits('onClickDownload');
          // if (option === 'forecastRequestMail') emits('onClickForecastRequestMail');
        }
      "
    />
    <button
      v-else-if="headerKey === 'removeFromForecastButton'"
      class="remove-from-forecast-button"
      @click="emits('onClickRemoveFromForecast')"
    >
      ✕
    </button>
    <span v-else class="table-cell-content" :class="{ 'table-cell-content': isBold }" @click="emits('onClickNarrow')">
      {{ forecastForReceiptTableContentVM.displayForecastForReceiptItem(headerKey, item, forecastStartYearMonth) }}
    </span>
  </TableCell>
</template>

<style scoped lang="scss">
.table-cell-content {
  font-family: Noto Sans JP;
  font-size: $fontSizeSS;
  font-weight: 400;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &--bold {
    font-weight: bold;
  }
}

.remove-from-forecast-button {
  display: grid;
  place-items: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: none;
  background-color: transparent;
  color: $semanticError;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  margin: 0 auto;

  &:hover {
    background-color: rgba($semanticError, 0.1);
  }
}

.transition-timeline-button {
  min-width: 80px;
  min-height: 18px;
  height: fit-content;
  border-radius: 4px;
  display: grid;
  place-items: center;
  background-color: $semanticWhite;
  border: 1px solid $semanticPrimary;
  padding: 3px 16px;
  cursor: pointer;

  &__content-text {
    font-size: $fontSizeSS;
    font-weight: 400;
    color: $semanticPrimary;
  }
}
</style>
